import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useHomeData } from "../hooks/useHomeData";
import { useLevelUpCelebration } from "../hooks/useLevelUpCelebration";
import { api, type ChatModeItem } from "../lib/api";
import { getChatModeDisplayName } from "../lib/chatModeDisplay";
import { logError } from "../lib/errors";
import { writePendingJobNotification } from "../lib/jobNotifications";
import {
  GOOGLE_SCOPE_BUNDLES,
  getGoogleIntegrationAggregateStatusDescription,
  getGoogleIntegrationAggregateStatusLabel,
  resolveGoogleIntegrationAggregateState,
} from "../lib/googleScopes";
import { getLineLinkStatusDescription, getLineLinkStatusLabel } from "../lib/lineLink";
import { storage } from "../lib/storage";

const FORCE_NEW_USER_BOOTSTRAP_KEY = "wizmate.force-new-user-bootstrap";

function findUnacknowledgedUnlockedMode(
  characterId: string | null,
  modes: ChatModeItem[]
): ChatModeItem | null {
  if (!characterId) return null;
  const acknowledged = storage.getAcknowledgedUnlockedModes(characterId);
  return modes.find((m) => m.mode !== 0 && m.unlocked && !acknowledged.includes(m.mode)) ?? null;
}

function getCharacterLastName(characterName?: string): string {
  if (!characterName) return "メイ";

  const normalized = characterName
    .replace(/[「」『』]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) return "メイ";

  const parts = normalized
    .split(/[ ・･._-]+/)
    .map((part) => part.trim())
    .filter(Boolean);

  return parts.length > 1 ? parts[parts.length - 1] : parts[0];
}

type MenuButtonProps = {
  title: string;
  description?: string;
  status?: string;
  disabled?: boolean;
  destructive?: boolean;
  onClick?: () => void;
};

function MenuButton({ title, description, status, disabled, destructive, onClick }: MenuButtonProps) {
  const resolvedTitle = title === "プロフィール設定" ? "プロファイル設定" : title;
  const resolvedDescription =
    title === "プロフィール設定" ? "あなたの情報を設定できます。" : description;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-start rounded-[1.1rem] px-4 py-3 text-left transition ${
        disabled ? "cursor-not-allowed opacity-55" : "hover:bg-[#FDF8F5]"
      }`}
    >
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span className={`text-sm font-bold ${destructive ? "text-[#A1493E]" : "text-[#5D4D46]"}`}>{resolvedTitle}</span>
          {status ? (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-black ${
                disabled
                  ? "border border-[#E7DDD6] bg-[#F7F2EE] text-[#A1887D]"
                  : "border border-[#E6DACD] bg-[#FFF8F1] text-[#A46D4C]"
              }`}
            >
              {status}
            </span>
          ) : null}
        </span>
        {resolvedDescription ? (
          <span className="mt-1 block text-xs font-medium leading-5 text-[#7B6861]">{resolvedDescription}</span>
        ) : null}
      </span>
    </button>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const {
    loading,
    error,
    vm,
    currentCharacterId,
    todayJobLogs,
    yesterdayJobLogs,
    messageToUser,
    intimacyPercent,
    hasNewMemo,
    lineLinkSnapshot,
    googleIntegrationViews,
    refresh,
  } = useHomeData();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [portraitSrc, setPortraitSrc] = useState("/img/no-image.png");
  const [showYesterday, setShowYesterday] = useState(false);
  const [expandedJobLogs, setExpandedJobLogs] = useState(false);

  const characterLastName = getCharacterLastName(vm?.characterName);
  const pageSize = 4;
  const displayedJobLogs = showYesterday ? yesterdayJobLogs : todayJobLogs;
  const hasOlderLogs = yesterdayJobLogs.length > 0;
  const visibleJobLogs = expandedJobLogs ? displayedJobLogs : displayedJobLogs.slice(0, pageSize);
  const hiddenCount = displayedJobLogs.length - pageSize;

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    setExpandedJobLogs(false);
  }, [showYesterday]);

  useEffect(() => {
    setPortraitSrc(vm?.imageUrl || "/img/no-image.png");
  }, [vm?.imageUrl]);

  useEffect(() => {
    if (loading || !vm || !currentCharacterId) return;

    (async () => {
      try {
        const response = await api.getChatModes({ characterId: currentCharacterId });
        const notification = findUnacknowledgedUnlockedMode(currentCharacterId, response.modes);
        if (notification) {
          writePendingJobNotification({
            kind: "mode_unlock",
            characterId: currentCharacterId,
            mode: notification.mode,
            modeName: getChatModeDisplayName(notification),
          });
        }
      } catch {
        // ホーム表示は継続する
      }
    })();
  }, [loading, vm, currentCharacterId]);

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const openCharacterSettings = () => {
    closeMenu();
    navigate("/character-switch");
  };

  const openProfileSettings = () => {
    closeMenu();
    navigate("/profile", { state: { returnTo: "/home" } });
  };

  const openLineLink = () => {
    closeMenu();
    navigate("/line-link", { state: { returnTo: "/home" } });
  };

  const openGoogleLink = () => {
    closeMenu();
    navigate(`/google-link?returnTo=${encodeURIComponent("/home")}&mode=selector`, {
      state: { returnTo: "/home", mode: "selector" },
    });
  };

  const handleInitialize = async () => {
    closeMenu();
    const ok = window.confirm("初期化します。よろしいですか？");
    if (!ok) return;

    const auth = storage.getAuth();
    if (auth.userId && auth.accessToken) {
      try {
        await api.invalidateGoogleSso({ reason: "user_initialize" });
      } catch (e) {
        logError(e);
      }
    }

    storage.clearAuth();
    storage.clearGoogleSso();
    localStorage.removeItem("app_device_id");
    localStorage.setItem(FORCE_NEW_USER_BOOTSTRAP_KEY, String(Date.now()));

    window.location.replace("/splash");
  };

  const lineLinkStatusLabel = getLineLinkStatusLabel(lineLinkSnapshot);
  const lineLinkDescription = getLineLinkStatusDescription(lineLinkSnapshot);
  const bundleGoogleConnected =
    googleIntegrationViews[GOOGLE_SCOPE_BUNDLES.bundle].state === "connected";
  const googleAggregateStatus = bundleGoogleConnected
    ? "full"
    : resolveGoogleIntegrationAggregateState({
        [GOOGLE_SCOPE_BUNDLES.receipt]:
          googleIntegrationViews[GOOGLE_SCOPE_BUNDLES.receipt].state === "connected",
        [GOOGLE_SCOPE_BUNDLES.news]:
          googleIntegrationViews[GOOGLE_SCOPE_BUNDLES.news].state === "connected",
      });
  const googleLinkStatusLabel = getGoogleIntegrationAggregateStatusLabel(googleAggregateStatus);
  const googleLinkDescription = getGoogleIntegrationAggregateStatusDescription(googleAggregateStatus);

  useLevelUpCelebration(vm?.rankName, currentCharacterId, "home");

  return (
    <div className="min-h-screen bg-[#FDF8F5] text-[#4A3E39] antialiased">
      <div className="mx-auto min-h-screen w-full max-w-md px-6 pb-6 pt-10 sm:px-8">
        <header className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-[#7C5A4E]">WizMate</h1>
            <p className="mt-0.5 text-[11px] font-bold tracking-[0.1em] text-[#A67C52]">PERSONAL CONCIERGE</p>
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setIsMenuOpen((prev) => !prev)}
              aria-label="メニュー"
              aria-expanded={isMenuOpen}
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#EADBC8] bg-white/70 shadow-sm"
            >
              <span className="sr-only">メニュー</span>
              <span className="flex flex-col items-center justify-center gap-1">
                <span className="block h-0.5 w-5 rounded bg-[#7C5A4E]" />
                <span className="block h-0.5 w-5 rounded bg-[#7C5A4E]" />
                <span className="block h-0.5 w-5 rounded bg-[#7C5A4E]" />
              </span>
            </button>

            {isMenuOpen ? (
              <>
                <button
                  type="button"
                  aria-label="メニューを閉じる"
                  className="fixed inset-0 z-10 cursor-default bg-transparent"
                  onClick={closeMenu}
                />

                <div className="absolute right-0 top-14 z-20 w-64 rounded-[1.6rem] border border-[#EADBC8] bg-white/95 p-2 shadow-[0_18px_40px_rgba(124,90,78,0.18)] backdrop-blur-sm">
                  <MenuButton
                    title="プロフィール設定"
                    description="メイに伝わるあなたの情報を整えます。"
                    onClick={openProfileSettings}
                  />
                  <MenuButton
                    title="キャラクター設定"
                    description="見た目や声を切り替えます。"
                    onClick={openCharacterSettings}
                  />
                  <MenuButton
                    title="LINE連携"
                    description={lineLinkDescription}
                    status={lineLinkStatusLabel}
                    onClick={openLineLink}
                  />
                  <MenuButton
                    title="Google連携"
                    description={googleLinkDescription}
                    status={googleLinkStatusLabel}
                    onClick={openGoogleLink}
                  />
                  <MenuButton
                    title="コイン購入"
                    description="今後の体験向上に向けて準備中です。"
                    status="準備中"
                    disabled={true}
                  />
                  <MenuButton
                    title="アイテム交換"
                    description="今後の体験向上に向けて準備中です。"
                    status="準備中"
                    disabled={true}
                  />

                  <hr className="mx-4 my-1 border-[#EADBC8]" />

                  <MenuButton title="初期化" destructive={true} onClick={handleInitialize} />

                  {process.env.REACT_APP_VERSION ? (
                    <p className="px-4 pb-1 pt-2 text-center text-[10px] font-medium text-[#C4AFA8]">
                      v{process.env.REACT_APP_VERSION}
                    </p>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </header>

        <main className="flex flex-1 flex-col items-center">
          {loading ? <p className="mt-3 text-center text-xs text-[#8E6E60]">読み込み中です...</p> : null}
          {!loading && error ? <p className="mt-3 text-center text-xs text-[#AF3B2E]">{error}</p> : null}

          {vm ? (
            <>
              <section className="relative mb-6 mt-2 flex w-full flex-col items-center">
                <div className="absolute -right-4 -top-8 h-36 w-36 rounded-full bg-[#F4E3D7] opacity-60 blur-3xl" />

                <div className="relative h-64 w-64 overflow-hidden rounded-full border-[6px] border-white bg-white shadow-[0_10px_25px_rgba(166,124,82,0.2)] animate-[bounce_5s_infinite]">
                  <img
                    src={portraitSrc}
                    alt={vm.characterName || "WizMate character"}
                    onError={() => {
                      if (portraitSrc !== "/img/no-image.png") {
                        setPortraitSrc("/img/no-image.png");
                      }
                    }}
                    className="h-full w-full object-cover object-top"
                  />
                </div>

                <div className="hidden absolute right-0 top-4 rounded-full bg-[#D1907B] px-4 py-2 text-[12px] font-black text-white shadow-lg">
                  {hasNewMemo ? "新着メモ" : "相談OK"}
                </div>

                <div className="relative -mt-4 w-full max-w-[18rem] rounded-[1.8rem] border border-white/90 bg-white px-5 py-4 text-center shadow-[0_16px_30px_rgba(166,124,82,0.15)]">
                  <span className="absolute left-10 top-[-10px] h-5 w-5 rotate-45 border-l border-t border-white/90 bg-white" />
                  <p className="whitespace-pre-line text-[15px] font-bold leading-7 text-[#5D4D46]">
                    {messageToUser || "今日はお話ししますか？ お仕事も手伝えます。"}
                  </p>
                </div>
              </section>

              <section className="mb-6 w-full rounded-[1.6rem] border border-[#EADBC8] bg-white/65 px-5 py-4">
                <div className="mb-2 flex items-center justify-between text-[12px] font-bold text-[#A67C52]">
                  <span>{vm.characterName}</span>
                  <span>{vm.rankName || "--"}</span>
                </div>

                <div className="h-2 w-full rounded-full bg-[#F0E7DF]">
                  <div
                    className="h-2 rounded-full bg-[#5D4D46] transition-all duration-500"
                    style={{ width: `${intimacyPercent}%` }}
                    aria-label="intimacy progress"
                  />
                </div>

                {vm.description ? <p className="mt-2 text-[13px] font-medium text-[#6B5851]">{vm.description}</p> : null}
              </section>

              <section className="mb-8 grid w-full grid-cols-2 gap-3">
                <Link
                  to="/talk"
                  className="rounded-[1.8rem] bg-[#5D4D46] px-4 py-5 text-center text-[#FDF8F5] shadow-xl transition-all hover:bg-[#4A3E39] active:scale-[0.98]"
                >
                  <span className="block text-lg font-black tracking-wide">{characterLastName}と話す</span>
                  <span className="mt-1 block text-xs font-medium text-[#F6E8DE]">会話・壁打ち・作戦会議へ</span>
                </Link>

                <Link
                  to="/request"
                  className="rounded-[1.8rem] border border-[#D9C5B9] bg-white/85 px-4 py-5 text-center text-[#5D4D46] shadow-sm transition-all hover:bg-white active:scale-[0.98]"
                >
                  <span className="block text-lg font-black tracking-wide">{characterLastName}に頼む</span>
                  <span className="mt-1 block text-xs font-medium text-[#8B7065]">情報収集・整理・手配をおまかせ</span>
                </Link>
              </section>

              <section className="w-full rounded-[2rem] border border-[#EADBC8] bg-white/40 p-6">
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-[13px] font-black uppercase tracking-widest text-[#A67C52]">
                    {characterLastName}からのメモ{showYesterday ? "・前日分" : ""}
                  </span>
                  <div className="h-px flex-1 bg-[#EADBC8]" />
                  {hasOlderLogs ? (
                    <button
                      type="button"
                      onClick={() => setShowYesterday((prev) => !prev)}
                      className="shrink-0 text-[11px] font-black text-[#A67C52] underline"
                    >
                      {showYesterday ? "今日を見る" : "前日を見る"}
                    </button>
                  ) : null}
                </div>

                <ul className="space-y-3">
                  {displayedJobLogs.length > 0 ? (
                    <>
                      {visibleJobLogs.map((log, idx) => (
                        <li
                          key={log.id}
                          className={`flex items-start justify-between ${idx > 0 ? "border-t border-white/50 pt-3" : ""}`}
                        >
                          <span className="whitespace-pre-line pr-2 text-[15px] font-bold leading-snug text-[#5D4D46]">
                            ・{log.text}
                          </span>
                          <span className="ml-2 mt-1 shrink-0 text-[11px] font-black text-[#A67C52]">{log.time}</span>
                        </li>
                      ))}
                      {!expandedJobLogs && hiddenCount > 0 ? (
                        <li>
                          <button
                            type="button"
                            onClick={() => setExpandedJobLogs(true)}
                            className="text-[11px] font-black text-[#A67C52] underline"
                          >
                            もっと見る（{hiddenCount}件）
                          </button>
                        </li>
                      ) : null}
                    </>
                  ) : (
                    <li className="text-[14px] font-medium text-[#7B6861]">まだメモはありません。</li>
                  )}
                </ul>
              </section>
            </>
          ) : null}
        </main>

        <footer className="w-full py-8 text-center opacity-30">
          <p className="font-mono text-[10px] uppercase tracking-widest">system: active / mode: calm</p>
        </footer>
      </div>
    </div>
  );
}
